import { tokens } from '@fluentui/react-components';
import {
    type ColumnDef,
    type ColumnFiltersState,
    type SortingState,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import type { Row, getExpandedRowModel, getFilteredRowModel, getSortedRowModel } from '@tanstack/react-table';
import { useCallback } from 'react';
import { dateFilter } from '../../data-table/header/date/filter/index.js';
import { numberFilter } from '../../data-table/header/number/filter/index.js';
import { selectFilter } from '../../data-table/header/select/filter.js';
import { stringArrayFilter } from '../../data-table/header/string-array/filter/index.js';
import { stringFilter } from '../../data-table/header/string/filter/index.js';
import { useColumnDrag } from './_shared/column-drag.js';
import { useColumnSizes } from './_shared/column-sizes.js';
import { useColumnVisibility } from './_shared/column-visibility.js';
import { useResizableTableContext } from './_shared/context.js';
import { useRowExpansion } from './_shared/row-expand.js';
import { useRowSelection } from './_shared/row-selection.js';

type UseResizableTableProps<TData, TValue> = {
    id: string;
    data: TData[];
    columns: ColumnDef<TData, TValue>[];
    getItemId: (item: TData) => string;
    fixedFirstColumn?: boolean;
    maxWidth?: number;
    expand?: {
        getModel: typeof getExpandedRowModel;
        getSubRows: (item: TData) => TData[];
    };
    filter?: {
        default?: ColumnFiltersState;
        getModel: typeof getFilteredRowModel;
        // onChange?: TODO;
    };
    selection?: {
        enabled: ((row: Row<TData>) => boolean) | boolean;
        disableAutoSelection?: boolean;
        onChange?: (updateValues: string[], currentValues: string[]) => void;
        setHighlights?: (updateValues: string[]) => string[];
    };
    sort?: {
        default?: SortingState;
        getModel: typeof getSortedRowModel;
    };
};

export function useResizableTable<TData, TValue>({
    id,
    data,
    columns,
    getItemId,
    fixedFirstColumn,
    maxWidth,
    expand,
    filter,
    selection,
    sort,
}: UseResizableTableProps<TData, TValue>) {
    // set up column order
    const { columnOrder, setColumnOrder, resetColumnOrder } = useColumnDrag({
        id,
        columns,
        fixedFirstColumn: !!fixedFirstColumn,
    });

    // set up column visibility
    const { columnVisibility, setColumnVisibility, resetColumnVisibility } = useColumnVisibility({ id, columns });

    // set up row expansion
    const { expandedRows, setExpandedRows, resetExpandedRows, updateExpandedRows } = useRowExpansion({ id });

    // set up row selection
    const { rowSelectionHighlights, rowSelection, setRowSelection, updateRowSelection } = useRowSelection({
        disableAutoRowSelection: selection?.disableAutoSelection,
        setRowSelectionHighlights: selection?.setHighlights,
        onRowSelectionChange: selection?.onChange,
    });

    // set up table
    const table = useReactTable({
        data: data,
        columns: columns,
        getRowId: getItemId,
        enableColumnResizing: true,
        getCoreRowModel: getCoreRowModel(),
        // expand rows
        getSubRows: expand?.getSubRows,
        getExpandedRowModel: expand?.getModel(),
        onExpandedChange: expand?.getModel ? updateExpandedRows : undefined,
        // sorting
        getSortedRowModel: sort?.getModel(),
        // column filters
        enableColumnFilters: !!filter?.getModel,
        getFilteredRowModel: filter?.getModel(),
        filterFns: {
            dateFilter,
            numberFilter,
            stringFilter,
            stringArrayFilter,
            selectFilter,
        },
        // initial state
        initialState: {
            columnFilters: filter?.default,
            sorting: sort?.default,
        },
        // controlled state
        state: {
            columnOrder,
            columnVisibility,
            expanded: expand?.getModel ? expandedRows : undefined,
            rowSelection,
        },
        // row visibility
        enableHiding: true,
        onColumnVisibilityChange: setColumnVisibility,
        // row selection
        enableRowSelection: selection?.enabled,
        enableMultiRowSelection: false,
        enableSubRowSelection: false,
        onRowSelectionChange: updateRowSelection,
    });

    const { hasInitialized, containerRef, tableRef, columnSizes, setColumnSizes, resetColumnSizes } = useColumnSizes(
        id,
        table
    );

    const context = useResizableTableContext({
        table,
        padContainerValue: tokens.spacingVerticalXXL,
        maxWidth,
        hasInitialized,
        containerRef,
        tableRef,
        rowSelection,
        rowSelectionHighlights,
        columnSizes,
        columnOrder,
        columnVisibility,
    });

    const resetTableLayout = useCallback(() => {
        resetColumnOrder();
        resetColumnVisibility();
        requestAnimationFrame(resetColumnSizes);
        requestAnimationFrame(resetExpandedRows);
    }, [resetColumnOrder, resetColumnVisibility, resetColumnSizes, resetExpandedRows]);

    return {
        // table
        table,
        resetTableLayout,
        // context
        context,
        // column order
        columnOrder,
        setColumnOrder,
        resetColumnOrder,
        // column visibility
        columnVisibility,
        setColumnVisibility,
        resetColumnVisibility,
        // row expansion
        expandedRows,
        setExpandedRows,
        resetExpandedRows,
        // row selection
        rowSelectionHighlights,
        rowSelection,
        setRowSelection,
        // column sizes
        hasInitialized,
        containerRef,
        tableRef,
        columnSizes,
        setColumnSizes,
        resetColumnSizes,
    };
}
